/**
 * ROI Metrics Fixtures for Testing
 * Sample data for Review, Verify, and Insight products
 * Task: 112-121-T001
 */

import type { ROIMetricsBlock } from '~/types/roi';

/**
 * Review product ROI metrics
 * Focus: Time saved, consistency, risk reduction
 */
export const REVIEW_ROI_FIXTURES: ROIMetricsBlock = {
  metrics: [
    {
      label: 'Review Time Saved',
      value: 65,
      unit: '%',
      icon: 'bolt',
      description: 'per document review cycle',
    },
    {
      label: 'Error Reduction',
      value: 92,
      unit: '%',
      icon: 'shield-check',
      description: 'in document quality checks',
    },
    {
      label: 'Compliance Coverage',
      value: 99.8,
      unit: '%',
      icon: 'document-check',
      description: 'regulatory requirement validation',
    },
  ],
  substantiation: [
    {
      title: 'Case Study: Financial Services Firm',
      url: '/resources/case-study-review-finance',
      type: 'internal',
    },
    {
      title: 'White Paper: Document Review Best Practices',
      url: 'https://qiksolve.com/wp/document-review-whitepaper.pdf',
      type: 'external',
    },
  ],
};

/**
 * Verify product ROI metrics
 * Focus: Accuracy, time to deployment, risk mitigation
 */
export const VERIFY_ROI_FIXTURES: ROIMetricsBlock = {
  metrics: [
    {
      label: 'Deployment Speed',
      value: 3,
      unit: '×',
      icon: 'rocket-launch',
      description: 'faster verification cycles',
    },
    {
      label: 'Bug Detection Rate',
      value: 87,
      unit: '%',
      icon: 'exclamation-circle',
      description: 'of critical issues caught pre-launch',
    },
    {
      label: 'Rollback Prevention',
      value: 78,
      unit: '%',
      icon: 'arrow-uturn-left',
      description: 'reduction in production incidents',
    },
  ],
  substantiation: [
    {
      title: 'Verify Success Story',
      url: '/resources/case-study-verify-tech',
      type: 'internal',
    },
    {
      title: 'ROI Calculator',
      url: '/tools/roi-calculator-verify',
      type: 'internal',
    },
  ],
};

/**
 * Insight product ROI metrics
 * Focus: Decision speed, data confidence, revenue impact
 */
export const INSIGHT_ROI_FIXTURES: ROIMetricsBlock = {
  metrics: [
    {
      label: 'Decision Time',
      value: 8,
      unit: 'hours',
      icon: 'clock',
      description: 'vs. 2-3 days manual analysis',
    },
    {
      label: 'Data Accuracy',
      value: 96,
      unit: '%',
      icon: 'check-circle',
      description: 'in predictive insights',
    },
    {
      label: 'Revenue Uplift',
      value: 23,
      unit: '%',
      icon: 'trending-up',
      description: 'average from insight-driven decisions',
    },
  ],
  substantiation: [
    {
      title: 'Insight Analytics Overview',
      url: '/resources/insight-analytics-guide',
      type: 'internal',
    },
    {
      title: 'Dashboard Examples',
      url: '/demo/insight-dashboards',
      type: 'internal',
    },
  ],
};

/**
 * Fixture with intentionally missing/invalid data for error handling tests
 */
export const INVALID_ROI_FIXTURES: ROIMetricsBlock = {
  metrics: [
    {
      label: 'Valid Metric',
      value: 42,
      unit: '%',
      icon: 'star',
    },
    {
      label: '', // Missing label
      value: 99,
      unit: '%',
      icon: 'bolt',
    } as any,
    {
      label: 'Missing Value',
      value: undefined as any, // Missing value
      unit: '%',
      icon: 'chart-bar',
    },
  ],
  substantiation: [
    {
      title: 'Valid Link',
      url: 'https://example.com',
      type: 'external',
    },
    {
      title: '', // Missing title
      url: 'https://example.com',
      type: 'external',
    } as any,
  ],
};
